<template>
    <v-card class="order-details-summary">
        <v-card-text class="order-details-summary-wrapper text-black text-subtitle-2">
            <v-row no-gutters>
                <v-col :cols="12" class="order-details-summary__main" md="12">
                    <v-row
                        v-if="(isTO1 || isTO2 || isAgency) && order.externalRefNumber"
                        no-gutters
                        class="order-details-summary__main-row order-details-summary__main-block justify-space-between flex-nowrap"
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label">
                            {{ `${$t('external_reservation_ref')}:` }}
                        </span>
                        <div class="order-details-summary__main-content text-end">
                            {{ order.externalRefNumber }}
                        </div>
                    </v-row>
                    <v-row
                        v-if="showAgentRefNumber"
                        no-gutters
                        align="center"
                        class="order-details-summary__main-row order-details-summary__main-ref order-details-summary__main-block mb-6 justify-space-between flex-nowrap"
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label">{{ `${$t('agent_reservation_ref')}:` }}</span>
                        <div
                            v-if="!editAgentRef"
                            class="order-details-summary__main-content d-flex align-center justify-end"
                            style="gap: 4px"
                        >
                            <template v-if="order.agencyInfo?.agentRefNumber">
                                {{ order.agencyInfo.agentRefNumber }}
                            </template>
                            <span v-else>{{ $t('add') }}</span>
                            <v-btn
                                v-if="!authStore.isAccountant"
                                size="small"
                                icon="mdi-pencil"
                                variant="text"
                                density="compact"
                                color="grey"
                                data-cy="add-agent-ref-number"
                                @click="clickEditAgentRef"
                            />
                        </div>
                        <div
                            v-else
                            style="padding-bottom: 2px"
                            class="order-details-summary__main-edit d-flex flex-grow-1"
                        >
                            <v-text-field
                                v-model="agentRefNumber"
                                :loading="agentRefNumberDisabled"
                                :disabled="agentRefNumberDisabled"
                                :placeholder="$t('enter_ref')"
                                density="compact"
                                hide-details
                                class="ma-0 text-subtitle-2 font-weight-regular"
                                data-cy="agent-ref-number"
                            >
                                <template v-slot:append>
                                    <v-icon color="error" size="small" @click="editAgentRef = !editAgentRef">
                                        mdi-close
                                    </v-icon>
                                    <v-icon color="primary" size="small" end @click="saveAgentRef">
                                        mdi-check-circle-outline
                                    </v-icon>
                                </template>
                            </v-text-field>
                        </div>
                    </v-row>
                    <v-row
                        v-if="order.manualBookingDate && $conf.public.account.showBookingDate"
                        no-gutters
                        class="order-details-summary__main-row order-details-summary__main-block mb-6 justify-space-between flex-nowrap"
                        align="center"
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label"> {{ $t('booking_date') }}: </span>
                        <div
                            v-if="!editBookingDateMode"
                            class="d-flex align-center justify-end text-end"
                            style="gap: 4px"
                        >
                            {{ $filters.dateFormat(order.manualBookingDate) }}
                            <v-btn
                                v-if="isBookingDateChangeable"
                                size="small"
                                icon="mdi-pencil"
                                variant="text"
                                density="compact"
                                color="grey"
                                data-cy="edit-booking-date"
                                @click="toggleEditBookingDateMode"
                            />
                        </div>
                        <template v-else>
                            <div class="order-details-summary__main-edit d-flex align-center">
                                <date-picker
                                    v-model="newBookingDate"
                                    class="cursor-pointer"
                                    hide-details
                                    :max="$dateFns.format(new Date())"
                                    :label="$t('booking_date')"
                                    outlined
                                    dense
                                />
                                <v-btn
                                    :disabled="!newBookingDate"
                                    :loading="bookingDateSaveLoading"
                                    icon="mdi-check-circle-outline"
                                    variant="text"
                                    color="success"
                                    density="compact"
                                    class="ms-2 cursor-pointer"
                                    data-cy="save-booking-date"
                                    @click="changeEditBookingDate"
                                />
                            </div>
                        </template>
                    </v-row>
                    <v-row
                        v-if="clientName"
                        class="order-details-summary__main-row order-details-summary__main-block mb-2 justify-space-between flex-nowrap"
                        no-gutters
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label"> {{ $t('client') }}: </span>
                        <div
                            v-if="!editClientMode"
                            class="d-flex align-center full-height justify-end"
                            style="gap: 4px"
                        >
                            <span class="order-details-summary__main-content" data-cy="client-name">
                                {{ clientName }}
                            </span>
                            <v-btn
                                size="small"
                                icon="mdi-information-outline"
                                variant="text"
                                density="compact"
                                color="grey"
                                data-cy="info-client"
                                @click="showContactDetailsModal('client')"
                            />
                            <v-btn
                                v-if="(isTO1 || isAgency) && !authStore.isAccountant"
                                size="small"
                                icon="mdi-pencil"
                                variant="text"
                                density="compact"
                                color="grey"
                                data-cy="edit-client-button"
                                @click="editClientMode = !editClientMode"
                            />
                        </div>
                        <order-details-edit-client
                            v-else
                            class="order-details-summary__main-edit mb-2 flex-grow-1"
                            :save-loading="clientSaveLoading"
                            :client-person="order.clientPerson"
                            @save="onClientChange"
                        />
                    </v-row>
                    <template v-if="order.clientCompanyName || tripAgentName">
                        <v-row
                            v-if="order.clientCompanyName"
                            no-gutters
                            class="order-details-summary__main-row order-details-summary__main-block mb-2 justify-space-between flex-nowrap"
                            style="gap: 10px"
                        >
                            <span class="order-details-summary__main-label">{{ $t('company') }}:</span>
                            <div
                                class="order-details-summary__main-content font-weight-regular d-flex align-center justify-end"
                                style="gap: 4px"
                            >
                                {{ order.clientCompanyName }}
                                <v-btn
                                    size="small"
                                    icon="mdi-information-outline"
                                    variant="text"
                                    density="compact"
                                    color="grey"
                                    data-cy="info-client-company"
                                    @click="showContactDetailsModal('company')"
                                />
                            </div>
                        </v-row>
                        <v-row
                            v-if="tripAgentName"
                            class="order-details-summary__main-row order-details-summary__main-block mb-2 justify-space-between flex-nowrap"
                            no-gutters
                            align="center"
                            style="gap: 10px"
                        >
                            <span class="order-details-summary__main-label"> {{ $t('agent') }}: </span>
                            <div class="order-details-summary__main-content order-details-summary__agent">
                                <div v-if="!editAgentMode" class="d-flex align-center justify-end" style="gap: 4px">
                                    {{ tripAgentName }}
                                    <v-btn
                                        v-if="isTO1 && !authStore.isAccountant"
                                        size="small"
                                        icon="mdi-pencil"
                                        variant="text"
                                        density="compact"
                                        color="grey"
                                        data-cy="edit-client-company"
                                        @click="editAgentMode = !editAgentMode"
                                    />
                                </div>
                                <order-details-edit-agent
                                    v-else
                                    :order-id="order.orderId"
                                    :agent-id="order.agent.id"
                                    :company-id="order.agentCompanyId"
                                    :save-loading="agentSaveLoading"
                                    @save="onAgentChange"
                                />
                            </div>
                        </v-row>
                    </template>
                    <div
                        v-if="tripOrganaizerName || isTO1 || isAgency || managerName"
                        class="order-details-summary__main-info mt-6"
                    >
                        <v-row
                            v-if="tripOrganaizerName"
                            class="order-details-summary__main-row order-details-summary__main-block mb-2 justify-space-between flex-nowrap"
                            no-gutters
                            align="center"
                            style="gap: 10px"
                        >
                            <span class="order-details-summary__main-label">{{ $t('trip_organizer') }}:</span>
                            <div
                                v-if="!editTripOrganaizerMode"
                                class="order-details-summary__main-content d-flex align-center full-height justify-end"
                                style="gap: 4px"
                            >
                                <span>
                                    {{ tripOrganaizerName }}
                                    <!--                                        <v-btn-->
                                    <!--                                            v-if="isTO1 || isAgency"-->
                                    <!--                                            size="small"-->
                                    <!--                                            icon="mdi-pencil"-->
                                    <!--                                            variant="text"-->
                                    <!--                                            color="primary"-->
                                    <!--                                            data-cy="edit-client-button"-->
                                    <!--                                            @click="editTripOrganaizerMode = !editTripOrganaizerMode"-->
                                    <!--                                        />-->
                                </span>
                            </div>
                            <order-details-edit-agent
                                v-else
                                class="order-details-summary__main-edit"
                                :agent-id="order.tripOrganizer.id"
                                :company-id="order.clientType === 'TA' ? order.clientPerson.companyId : order.clientId"
                                :save-loading="tripOrganaizerSaveLoading"
                                @save="onTripOrganizerChange"
                            />
                        </v-row>
                        <v-row
                            v-if="managerName && isShowManager"
                            class="order-details-summary__main-row order-details-summary__main-block mb-2 justify-space-between flex-nowrap"
                            no-gutters
                            align="center"
                            style="gap: 10px"
                        >
                            <span class="order-details-summary__main-label"> {{ $t('manager') }}:</span>
                            <div v-if="!editManagerMode" class="d-flex align-center justify-end" style="gap: 4px">
                                <span class="order-details-summary__main-content" data-cy="manager-name">
                                    {{ managerName }}
                                </span>
                                <v-btn
                                    v-if="isTO1 && !authStore.isAccountant"
                                    size="small"
                                    icon="mdi-pencil"
                                    variant="text"
                                    density="compact"
                                    color="grey"
                                    data-cy="edit-manager-button"
                                    @click="editManagerMode = !editManagerMode"
                                />
                            </div>
                            <order-details-edit-agent
                                v-else
                                class="order-details-summary__main-edit"
                                :order-id="order.orderId"
                                :agent-id="order.manager.id"
                                :company-id="person.companyId"
                                :save-loading="managerSaveLoading"
                                @save="onManagerChange"
                            />
                        </v-row>
                    </div>
                    <v-row
                        v-if="minDate || maxDate"
                        no-gutters
                        class="order-details-summary__main-block mt-6 justify-space-between flex-nowrap"
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label">{{ $t('dates') }}:</span>
                        <p class="order-details-summary__main-content text-end">
                            {{ $filters.dateFormat(minDate) }}
                            <span v-if="minDate !== maxDate"> - {{ $filters.dateFormat(maxDate) }}</span>
                        </p>
                    </v-row>
                    <v-row
                        v-if="duration"
                        class="order-details-summary__main-duration order-details-summary__main-row order-details-summary__main-row--no-mt"
                        no-gutters
                    >
                        <p class="order-details-summary__main-content full-width text-end">
                            {{ duration }}
                        </p>
                    </v-row>
                    <v-row
                        v-if="orderTravelers?.length"
                        no-gutters
                        class="order-details-summary__main-block mt-4 justify-space-between flex-nowrap"
                        style="gap: 10px"
                    >
                        <span class="order-details-summary__main-label"> {{ $t('travelers') }}:</span>
                        <p class="order-details-summary__main-content text-end">
                            <span v-if="adultsTravelers?.length">
                                {{ $t('adult', adultsTravelers.length) }} {{ adultsTravelers.length }}
                            </span>
                            <span v-if="childTravelers?.length"
                                >, {{ $t('child', childTravelers.length) }} {{ childTravelers.length }}
                            </span>
                        </p>
                    </v-row>
                    <v-row v-if="isMobile" no-gutters>
                        <v-col cols="12">
                            <order-destination-select
                                v-if="isTO1 && $conf.public.account.destinationInReservation"
                                class="justify-space-between"
                            />
                            <CampaignSelect
                                v-if="isTO1 && (personRole === 'director' || personRole === 'supervisor')"
                                class="justify-space-between"
                            />
                            <ClassificationSelect v-if="isTO1" class="justify-space-between" />
                        </v-col>
                    </v-row>
                    <template v-if="order.status !== 'NEW' && personRole !== 'dispatcher'">
                        <v-row v-if="isTourOperator" no-gutters class="order-details-summary__main-block mt-4">
                            <v-col
                                v-if="!$conf.public.account.licenseeForCorporateCompany"
                                cols="12"
                                class="order-details-summary__main-row order-details-summary__main-block"
                            >
                                <total-order-prices
                                    title="client_price"
                                    :order-prices="orderPricesCalculatedBrutto"
                                    :payment-status="customerPaymentStatus"
                                    :service-card-guarantee="gotPayAtHotelService(order)"
                                    :services="order.services"
                                    :vat-calculated-amount-by-each-service-brutto="
                                        vatCalculatedAmountByEachServiceBrutto
                                    "
                                >
                                    <cancel-penalty-label
                                        v-if="order.cancelPenalty"
                                        class="font-weight-regular"
                                        :price="getTotalPrice(orderPricesCalculatedBrutto)"
                                        :cancel-penalty="order.cancelPenalty"
                                    />
                                </total-order-prices>
                            </v-col>
                            <v-col
                                v-if="commissionAndNettoCheckbox"
                                class="order-details-summary__main-row order-details-summary__main-block"
                                cols="12"
                            >
                                <v-divider class="mt-2 pb-2" />
                                <total-order-prices
                                    :order-prices="orderPricesCalculatedNetto"
                                    :payment-status="supplierPaymentStatus"
                                    :service-card-guarantee="gotPayAtHotelService(order)"
                                    :services="order.services"
                                    :vat-calculated-amount-by-each-service-brutto="
                                        vatCalculatedAmountByEachServiceBrutto
                                    "
                                    title="supplier_price"
                                >
                                    <cancel-penalty-label
                                        v-if="order.cancelPenalty && commissionAndNettoCheckbox"
                                        class="font-weight-regular"
                                        :price="getTotalPrice(orderPricesCalculatedNetto)"
                                        :cancel-penalty="[]"
                                    />
                                </total-order-prices>
                                <v-divider class="mt-2 pb-2" />
                            </v-col>
                        </v-row>
                        <v-row
                            v-else
                            no-gutters
                            class="order-details-summary__main-row order-details-summary__main-block my-4"
                        >
                            <v-col cols="12">
                                <order-price-label
                                    :payment-status="isAgency ? supplierPaymentStatus : customerPaymentStatus"
                                    :order-prices="isAgency ? orderPricesCalculatedNetto : orderPricesCalculatedBrutto"
                                    :services="order.services"
                                    :vat-calculated-amount-by-each-service-netto="vatCalculatedAmountByEachServiceNetto"
                                    :cancel-penalties="order.cancelPenalty"
                                    :show-commission-and-netto-block="showCommissionAndNettoBlock"
                                >
                                    <cancel-penalty-label
                                        v-if="order.cancelPenalty"
                                        class="font-weight-regular"
                                        :price="
                                            getTotalPrice(
                                                isAgency ? orderPricesCalculatedNetto : orderPricesCalculatedBrutto
                                            )
                                        "
                                        :cancel-penalty="order.cancelPenalty"
                                    />
                                </order-price-label>
                            </v-col>
                        </v-row>
                        <v-row
                            v-if="isTourOperator && totalIncomeInClientCurrency?.amount && commissionAndNettoCheckbox"
                            no-gutters
                            class="justify-space-between flex-nowrap order-details-summary__main-block--income"
                            style="gap: 10px"
                        >
                            <span class="text-subtitle-2">{{ $t('income') }}</span>
                            <div class="text-end">
                                <p class="text-green font-weight-medium">
                                    +
                                    {{
                                        $filters.priceFormat(
                                            totalIncomeInClientCurrency.amount,
                                            totalIncomeInClientCurrency.currency
                                        )
                                    }}
                                </p>
                                <p v-if="totalIncomeInClientCurrency.percent" class="text-grey">
                                    {{ `${totalIncomeInClientCurrency.percent}%` }}
                                </p>
                            </div>
                        </v-row>
                        <v-row v-if="$conf.public.account.paymentScheduler && !isSupplier" class="pb-4 pt-4" no-gutters>
                            <div v-if="isManualInvoicesLoading" class="d-flex justify-center full-width">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <template v-else>
                                <v-col v-if="nextManualInvoice" cols="12">
                                    <div
                                        class="text-caption text-end"
                                        :class="{'text-error': nextManualInvoice.expired}"
                                    >
                                        {{ `${$t('next_payment_due')}:` }}
                                        {{
                                            $filters.priceFormat(
                                                nextManualInvoice.originalPrices[0].originalAmount,
                                                nextManualInvoice.originalPrices[0].originalAmountCurrency
                                            )
                                        }}
                                    </div>
                                    <div
                                        class="text-caption text-end"
                                        :class="{'text-error': nextManualInvoice.expired}"
                                    >
                                        {{ `${$t('before')} ${$filters.dateShortFormat(nextManualInvoice.dueDate)}` }}
                                    </div>
                                </v-col>
                                <v-btn
                                    v-if="!isTripAssistantKeyExist"
                                    color="primary"
                                    variant="text"
                                    size="small"
                                    class="px-0 ml-auto text-decoration-underline text-none"
                                    data-cy="view-payment-schedule"
                                    @click="isShowPaymentSchedule = true"
                                >
                                    <span v-if="!$conf.public.account.paymentSchedulerAutoPaymentMode">
                                        {{ $t('view_payment_schedule') }}
                                    </span>
                                    <span v-else>
                                        {{ $t('view_payments') }}
                                    </span>
                                </v-btn>
                            </template>
                            <template v-if="isShowAddPaymentButton">
                                <v-btn
                                    variant="outlined"
                                    class="my-2 text-black"
                                    color="grey-darken-1"
                                    block
                                    data-cy="add-service-button"
                                    :size="$breakpoint.smAndDown ? 'small' : 'default'"
                                    @click="isShowAddDepositModal = true"
                                    v-text="
                                        $conf.public.account.paymentSchedulerAutoPaymentMode
                                            ? $t('add_payment')
                                            : $t('add_payment_schedule')
                                    "
                                />
                            </template>
                        </v-row>
                    </template>
                </v-col>
                <v-col v-if="!isSupplier && !isGuest" :cols="12" md="12">
                    <v-row dense class="order-details-summary__main-block mb-4">
                        <v-col v-if="personRole !== 'dispatcher'" cols="6" class="py-0 d-flex flex-column align-start">
                            <order-custom-documents
                                ref="orderCustomDocuments"
                                :order="order"
                                :checkbox-services-selected="checkboxServicesSelected"
                            />
                            <v-btn
                                v-if="
                                    isB2B &&
                                    isShowInvoiceBtn &&
                                    showPaymentAndInvoiceStatus &&
                                    !gotPayAtHotelService(order) &&
                                    order.status !== 'NEW'
                                "
                                variant="text"
                                class="px-0"
                                @click="isAgency ? showInvoices('SUPPLIER') : showInvoices('CUSTOMER')"
                            >
                                <v-icon color="primary" start>mdi-receipt-text-outline</v-icon>
                                {{ $t('invoices.invoice') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="order-details-summary__btns-icon py-0 d-flex flex-column align-start">
                            <v-btn
                                v-if="voucherAvailable && isShowVoucherBtn"
                                :disabled="voucherLoading"
                                :loading="voucherLoading"
                                variant="text"
                                class="px-0"
                                @click="downloadVoucher"
                            >
                                <v-icon color="primary" start>mdi-file-document-outline</v-icon>
                                {{ $t('voucher') }}
                            </v-btn>
                            <v-btn
                                v-if="showItineraryBtn"
                                :disabled="loadItinerary"
                                :loading="loadItinerary"
                                variant="text"
                                class="px-0"
                                @click="getItinerary"
                            >
                                <v-icon color="primary" start>mdi-map-legend</v-icon>
                                {{ $t('itinerary') }}
                            </v-btn>
                            <v-btn
                                v-if="isTO1 && isReservationReportMailEnabled && hasOwnServices"
                                variant="text"
                                class="px-0"
                                @click="isShowReservationReportModal = true"
                            >
                                <v-icon color="primary" start>mdi-email-outline</v-icon>
                                {{ $t('reservation_report') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <div class="order-details-summary__controls d-flex flex-wrap justify-center">
                        <template
                            v-if="
                                showPaymentAndInvoiceStatus &&
                                canPayOrder &&
                                !gotPayAtHotelService(order) &&
                                !$conf.public.account.paymentScheduler
                            "
                        >
                            <v-btn
                                v-if="!isTO1"
                                variant="flat"
                                class="mb-2 text-white"
                                color="primary"
                                block
                                :size="$breakpoint.smAndDown ? 'small' : 'default'"
                                @click="isB2C ? payOrder('CUSTOMER') : payOrder('SUPPLIER')"
                            >
                                {{ $t('pay_order') }}
                            </v-btn>
                            <v-menu v-else>
                                <template v-slot:activator="{props}">
                                    <v-btn
                                        variant="flat"
                                        class="mb-2 text-white"
                                        color="primary"
                                        block
                                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                                        v-bind="props"
                                    >
                                        {{ $t('pay_order') }}
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-if="
                                            order.customerPaymentStatus !== 'PAID' &&
                                            order.customerPaymentStatus !== 'OVERPAID'
                                        "
                                        @click="payOrder('CUSTOMER')"
                                    >
                                        <v-list-item-title>
                                            {{ $t('client') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                        v-if="
                                            order.supplierPaymentStatus !== 'PAID' &&
                                            order.supplierPaymentStatus !== 'OVERPAID'
                                        "
                                        @click="payOrder('SUPPLIER')"
                                    >
                                        <v-list-item-title>{{ $t('accountType.SUPPLIER') }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <v-btn
                            v-if="!$conf.public.account.hideAddServiceBtn && personRole !== 'dispatcher'"
                            color="primary"
                            class="add-service mb-2 text-white"
                            block
                            data-cy="add-service-button"
                            :size="$breakpoint.smAndDown ? 'small' : 'default'"
                            @click="orderAddService"
                            v-text="$t('add_service')"
                        />
                        <template
                            v-if="
                                ((isB2B && !$conf.public.account.addOfflineServiceTO1Only) || isTO1) &&
                                personRole !== 'dispatcher'
                            "
                        >
                            <add-offline-service-dialog
                                ref="addOfflineService"
                                :order-travelers="orderTravelers"
                                :order-process-id="order.processId"
                                :client-type="order.clientType"
                                :order="order"
                                :client-person="order.clientPerson"
                                :customer-company-id="order.customerCompanyId"
                                @send-request="refreshOrder"
                            />
                            <p-n-r-import-dialog
                                v-if="$conf.public.account.addOfflineServicePNR"
                                @import-p-n-r="onImportPNR"
                            />
                        </template>
                        <v-btn
                            v-if="isTO1 || isTO2 || isAgency"
                            variant="text"
                            class="px-0"
                            data-cy="send-email"
                            @click="sendEmail"
                        >
                            <v-icon color="primary" start>mdi-email-outline</v-icon>
                            {{ $t('send_email') }}
                        </v-btn>
                        <order-add-deposit-modal
                            v-model="isShowAddDepositModal"
                            :object-id="order.objectId"
                            @confirm="onAddDepositConfirm"
                        />
                        <reservation-report-order-modal
                            v-if="isReservationReportMailEnabled"
                            v-model="isShowReservationReportModal"
                            :order="order"
                        />
                        <order-payment-schedule
                            ref="orderPaymentSchedule"
                            v-model="isShowPaymentSchedule"
                            :object-id="order.objectId"
                            :order-id="order.orderId"
                            :client-price-currencies="clientPriceCurrencies"
                            :_is-manual-invoices-loading="isManualInvoicesLoading"
                            :cancel-invoices-on-load="$conf.public.account.paymentSchedulerAutoPaymentMode"
                            @change-is-manual-invoices-loading="isManualInvoicesLoading = $event"
                            @load-manual-invoices="onManualInvoicesLoad"
                            @complete-payment="sendRequest"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, toNative, Inject} from 'vue-facing-decorator'
    import {downloadData} from '@/utils/helpers'
    import {convertPrice} from '@/utils/filters'
    import {VoucherMixin} from '@/components/account/mixins/VoucherMixin.vue'
    import {
        EventBus,
        PAY_ORDER_EVENT,
        SHOW_CONTACT_DETAILS_MODAL,
        SHOW_INVOICES_MODAL_EVENT,
        SHOW_SEND_EMAIL_MODAL,
    } from '@/utils/event-bus'
    import PaymentStatusLabel from '~src/components/account/snippets/paymentStatusLabel.src'
    import TotalOrderPrices from '~src/components/account/snippets/totalOrderPrices.src'
    import OrderPriceLabel from '~src/components/account/snippets/orderPriceLabel.src'
    import ChatModal from '@/components/modals/ChatModal.vue'
    import OrderAddServiceModal from '~src/components/account/modals/orderAddServiceModal.src'
    import AddOfflineServiceDialog from '~src/components/account/offlineServices/addOfflineServiceDialog.src'
    import PNRImportDialog from '~src/components/account/offlineServices/PNRImportDialog.vue'
    import OrderCustomDocuments from '~src/components/account/snippets/orderCustomDocuments.src'
    import OrderDetailsEditAgent from '@/components/account/OrderDetailsEditAgent.vue'
    import CancelPenaltyLabel from '@/components/snippets/CancelPenaltyLabel.vue'
    import OrderAddDepositModal from '~src/components/account/orderAddDepositModal.src'
    import OrderPaymentSchedule from '@/components/account/OrderPaymentSchedule.vue'
    import OrderDetailsEditClient from '@/components/account/OrderDetailsEditClient.vue'
    import {DocumentTemplatesMixin} from '@/components/account/mixins/DocumentTemplatesMixin.vue'
    import ReservationReportOrderModal from '@/components/account/modals/ReservationReportOrderModal.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import CampaignSelect from '~/components/account/orders/CampaignSelect.vue'
    import ClassificationSelect from '~/components/account/orders/ClassificationSelect.vue'
    import OrderDestinationSelect from '@/components/account/snippets/OrderDestinationSelect.vue'

    @Component({
        components: {
            OrderDestinationSelect,
            CampaignSelect,
            ClassificationSelect,
            ReservationReportOrderModal,
            OrderPaymentSchedule,
            OrderAddDepositModal,
            OrderDetailsEditClient,
            CancelPenaltyLabel,
            OrderDetailsEditAgent,
            OrderCustomDocuments,
            PaymentStatusLabel,
            TotalOrderPrices,
            OrderPriceLabel,
            ChatModal,
            OrderAddServiceModal,
            AddOfflineServiceDialog,
            PNRImportDialog,
            DatePicker,
        },
    })
    export class OrderDetailsSummary extends mixins(VoucherMixin, DocumentTemplatesMixin) {
        @Prop({default: false, type: Boolean}) outlined
        @Prop({required: true}) orderTravelers
        @Prop() checkboxServicesSelected
        @Prop() vatCalculatedAmountByEachServiceBrutto
        @Prop() vatCalculatedAmountByEachServiceNetto
        @Prop() orderPricesCalculatedBrutto
        @Prop() orderPricesCalculatedNetto
        @Prop() totalIncomeInClientCurrency
        @Inject({default: false}) isTripAssistantKeyExist

        isShowAddDepositModal = false
        isShowReservationReportModal = false
        isShowPaymentSchedule = null

        editAgentMode = false
        editManagerMode = false
        newBookingDate = null
        editBookingDateMode = false

        loadItinerary = false
        agentSaveLoading = false
        managerSaveLoading = false
        tripOrganaizerSaveLoading = false
        bookingDateSaveLoading = false

        isManualInvoicesLoading = false
        nextManualInvoice = null

        editClientMode = false
        editTripOrganaizerMode = false
        clientSaveLoading = false
        newClient = {}

        editAgentRef = false
        agentRefNumberDisabled = false
        agentRefNumber = null

        async mounted() {
            await this.$store.restored
            await this.load()
            this.isLoading = true
            await this.loadDocumentTemplates()
            this.isLoading = false
        }

        async load() {
            await this.loadClientPerson()
            await this.loadManualInvoices()
        }

        async refreshOrder() {
            await runtimeStore.refreshOrder()
            this.$emit('init-service-order')
        }

        async onAddDepositConfirm({autoPaymentMode, invoiceId}) {
            if (autoPaymentMode) {
                if (invoiceId) {
                    await this.$refs?.orderPaymentSchedule?.initSilentPay(invoiceId)
                }

                this.isShowAddDepositModal = false
            } else {
                await this.loadManualInvoices()
            }
        }

        async loadManualInvoices() {
            return await this.$refs?.orderPaymentSchedule?.loadManualInvoices()
        }

        toggleEditBookingDateMode() {
            this.newBookingDate = this.order.manualBookingDate?.split(' ')[0]
            this.editBookingDateMode = !this.editBookingDateMode
        }

        async changeEditBookingDate() {
            this.bookingDateSaveLoading = true

            try {
                const rs = await this.$api.orders.put(this.order.orderId, {manualBookingDate: this.newBookingDate})
                runtimeStore.SET_ORDER_DETAILS({...this.order, manualBookingDate: rs.requestBookingDate})
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.editBookingDateMode = false
                this.bookingDateSaveLoading = false
            }
        }

        onManualInvoicesLoad(invoices) {
            const nextManualInvoice = invoices?.find(m => m.paymentStatus === 'BILL')
            if (nextManualInvoice) {
                const expired = this.$dateFns.isBefore(new Date(nextManualInvoice.dueDate), new Date())

                this.nextManualInvoice = {...nextManualInvoice, expired}
            }
        }

        async loadClientPerson() {
            try {
                if (this.order.clientType === 'TA' && !this.order.clientPerson) {
                    const {persons} = await this.$api.privateClients.get({
                        companyId: this.order.agentCompanyId,
                        id: this.order.orderClientInfo.clientId,
                        active: true,
                        limit: 1,
                    })

                    if (persons[0]) {
                        runtimeStore.SET_ORDER_DETAILS({...this.order, clientPerson: persons[0]})
                    }
                }

                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        @Emit()
        sendRequest() {}

        async onClientChange(rq) {
            this.clientSaveLoading = true

            try {
                await this.$api.updateCrmPerson.put(this.order.orderClientInfo.clientId, rq)

                runtimeStore.SET_ORDER_DETAILS({
                    ...this.order,
                    clientPerson: {...this.order.clientPerson, ...rq},
                })

                this.editClientMode = false
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.clientSaveLoading = false
            }
        }

        async onAgentChange(agent) {
            this.agentSaveLoading = true

            try {
                await this.$api.orders.put(this.order.orderId, {agentId: agent.id})

                runtimeStore.SET_ORDER_DETAILS({...this.order, agent})

                this.editAgentMode = false
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.agentSaveLoading = false
            }
        }

        async saveAgentRef() {
            this.agentRefNumberDisabled = true
            try {
                await this.$api.orders.put(this.order.orderId, {agencyInfo: {agentRefNumber: this.agentRefNumber}})

                runtimeStore.SET_ORDER_DETAILS({...this.order, agencyInfo: {agentRefNumber: this.agentRefNumber}})
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.editAgentRef = !this.editAgentRef
                this.agentRefNumberDisabled = false
            }
        }

        async onManagerChange(manager) {
            this.managerSaveLoading = true

            try {
                await this.$api.orders.put(this.order.orderId, {managerId: manager.id})

                runtimeStore.SET_ORDER_DETAILS({...this.order, manager})

                this.editManagerMode = false
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.managerSaveLoading = false
            }
        }

        async onTripOrganizerChange(tripOrganizer) {
            this.tripOrganaizerSaveLoading = true

            try {
                // await this.$api.orders.put(this.order.orderId, {managerId: tripOrganizer.id})

                runtimeStore.SET_ORDER_DETAILS({...this.order, tripOrganizer})

                this.editTripOrganaizerMode = false
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.tripOrganaizerSaveLoading = false
            }
        }

        @Emit()
        orderAddService() {}

        getTotalPrice(prices) {
            const total = {amount: 0}

            prices.forEach(e => {
                const convertedPrice = convertPrice(e)

                if (!total.currency) {
                    total.currency = convertedPrice.currency
                }

                total.amount += convertedPrice.amount
            })

            return total
        }

        showInvoices(invoiceType) {
            EventBus.$emit(SHOW_INVOICES_MODAL_EVENT, {
                orderId: this.order.orderId,
                objectId: this.order.objectId,
                invoiceType: invoiceType,
                order: this.order,
            })
        }

        payOrder(invoiceType = 'SUPPLIER') {
            EventBus.$emit(PAY_ORDER_EVENT, {orderId: this.order.orderId, objectId: this.order.objectId, invoiceType})
        }

        gotPayAtHotelService(order) {
            if (order.services && order.services.length === 1) {
                return order.services.some(service =>
                    service.serviceDetailsContainer.accommodation
                        ? service.serviceDetailsContainer.accommodation.cardGuarantee
                        : false
                )
            }
            return false
        }

        async getItinerary() {
            this.loadItinerary = true
            try {
                const data = await this.$api.itinerary.get({
                    orderId: this.order.orderId,
                })
                downloadData(data, `Itinerary-${this.order.orderId}.pdf`)
            } catch (error) {
                this.$toast.error(error.errors[0].message)
            } finally {
                this.loadItinerary = false
            }
        }

        sendEmail() {
            const {templates} = this.$refs.orderCustomDocuments
            EventBus.$emit(SHOW_SEND_EMAIL_MODAL, {
                order: this.order,
                service: null,
                templates,
                checkboxServicesSelected: this.checkboxServicesSelected,
            })
        }

        showContactDetailsModal(type) {
            EventBus.$emit(SHOW_CONTACT_DETAILS_MODAL, {type, order: this.order})
        }

        clickEditAgentRef() {
            this.editAgentRef = !this.editAgentRef
            this.agentRefNumber = this.order.agencyInfo?.agentRefNumber || null
        }

        async onImportPNR(rqData) {
            if (this.$refs.addOfflineService) {
                this.$refs.addOfflineService.PNRRequestDataInit = rqData
                this.$refs.addOfflineService.dialog = true
            }
        }

        get isBookingDateChangeable() {
            return this.isTO1 && ['to1l.director'].includes(authStore.person.role) && !authStore.isAccountant
        }

        get clientPriceCurrencies() {
            return this.order.services.reduce((currencies, service) => {
                const serviceClientPriceCurrency = service.salesTerms.find(({type}) => type === 'CLIENT')?.price
                    ?.currency

                if (!currencies.includes(serviceClientPriceCurrency)) {
                    currencies.push(serviceClientPriceCurrency)
                }

                return currencies
            }, [])
        }

        get isShowAddPaymentButton() {
            if (!this.$conf.public.account.paymentScheduler) {
                return false
            }
            if (this.$conf.public.account.paymentSchedulerAutoPaymentMode) {
                return true
            } else {
                return this.isTO1 || this.isTO2 || this.isAgency
            }
        }

        get isShowInvoiceBtn() {
            return this.$conf.public.account.showOrderInvoiceBtn
        }

        get isB2C() {
            return authStore.isB2C
        }

        get commissionAndNettoCheckbox() {
            return persistentStore.commissionAndNettoCheckbox
        }

        get order() {
            return runtimeStore.orderDetails
        }

        get showPaymentAndInvoiceStatus() {
            return authStore.showPaymentAndInvoiceStatus
        }

        get showCommissionAndNettoBlock() {
            return (
                this.isAgency &&
                this.orderPricesCalculatedNetto.some(orderPrice => !!orderPrice.commission) &&
                this.commissionAndNettoCheckbox
            )
        }

        get canPayOrder() {
            return (
                (((authStore.isAgency || authStore.isB2C) &&
                    !['PAID', 'OVERPAID'].includes(this.order.supplierPaymentStatus)) ||
                    (authStore.isTO1 &&
                        (!['PAID', 'OVERPAID'].includes(this.order.supplierPaymentStatus) ||
                            !['PAID', 'OVERPAID'].includes(this.order.customerPaymentStatus)))) &&
                ['COMPLETED', 'IN_PROGRESS'].includes(this.order.status)
            )
        }

        get supplierPaymentStatus() {
            return this.order.supplierPaymentStatus
        }

        get customerPaymentStatus() {
            return this.order.customerPaymentStatus
        }

        get person() {
            return authStore.person
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isSupplier() {
            return authStore.companyType === 'INTERNAL_SUPPLIER'
        }

        get isGuest() {
            return authStore.isGuest
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get personRole() {
            return authStore.personRole
        }

        get managerName() {
            if (this.order.manager && (this.order.manager.lastName || this.order.manager.firstName)) {
                return `${this.order.manager.prefix || ''} ${this.order.manager.firstName || ''} ${
                    this.order.manager.lastName || ''
                }`
            }
            return null
        }

        get isShowManager() {
            return true
        }

        get tripOrganaizerName() {
            if (this.order.tripOrganizer && (this.order.tripOrganizer.firstName || this.order.tripOrganizer.lastName)) {
                return `${this.order.tripOrganizer.prefix || ''} ${this.order.tripOrganizer.firstName || ''} ${
                    this.order.tripOrganizer.lastName || ''
                }`
            }
            return null
        }

        get tripAgentName() {
            if (this.order.agent && (this.order.agent.firstName || this.order.agent.lastName)) {
                return `${this.order.agent.prefix || ''} ${this.order.agent.firstName || ''} ${
                    this.order.agent.lastName || ''
                }`
            }
            return null
        }

        get clientName() {
            if (this.order.clientPerson) {
                return `${this.order.clientPerson.prefix || ''} ${this.order.clientPerson.firstName} ${
                    this.order.clientPerson.lastName
                }`
            }
            return ''
        }

        get servicesForDates() {
            let services = this.order?.services
                .filter(s => s.serviceDetails?.[0]?.extraServiceProductType !== 'BANK_FEE')
                .filter(s => this.checkboxServicesSelected.includes(s.serviceId))

            if (!services?.every(s => s.status === 'Canceled' || s.status === 'Rejected')) {
                services = services?.filter(s => s.status !== 'Canceled' && s.status !== 'Rejected')
            }

            return services
        }

        get minDate() {
            if (this.servicesForDates?.length) {
                const dates = this.servicesForDates.map(service => {
                    return new Date(service.startDateTime)
                })
                return dates.sort((a, b) => a - b)[0]
            }
            return null
        }

        get maxDate() {
            if (this.servicesForDates?.length) {
                const dates = this.servicesForDates.map(service => {
                    return new Date(service.endDateTime)
                })
                return dates.sort((a, b) => a - b)[dates.length - 1]
            }
            return null
        }

        get duration() {
            if (!this.maxDate && !this.minDate) return null

            const day = this.$dateFns.differenceInCalendarDays(new Date(this.maxDate), new Date(this.minDate)) + 1
            const night = day > 1 ? day - 1 : 1
            if (night > 1) return day + ` ${this.$t('day', day)} / ` + night + ` ${this.$t('night', night)}`
            return day + ` ${this.$t('day', day)}`
        }

        get voucherAvailable() {
            return this.order.services.some(service => runtimeStore.isServiceVoucherAvailable(service))
        }

        get sortedServices() {
            return this.getSortedServices(this.order.services)
        }

        get showItineraryBtn() {
            return !this.$conf.public.account.hideItineraryBtn && this.order.status !== 'NEW'
        }

        get isReservationReportMailEnabled() {
            return this.$conf.public.reservationReport
        }

        get hasOwnServices() {
            return this.order.services.some(service => runtimeStore.ownProduct(service.supplierCode))
        }

        get isShowVoucherBtn() {
            return true
        }

        get adultsTravelers() {
            return this.orderTravelers?.filter(({isChild}) => !isChild)
        }

        get childTravelers() {
            return this.orderTravelers?.filter(({isChild}) => isChild)
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get showAgentRefNumber() {
            const TOValidation =
                this.order.orderType === 'TO' && this.order.clientType === 'DIRECT_SALES' ? false : true
            return (
                this.$conf.public.reservationFilters.agentRefNumber &&
                TOValidation &&
                (this.isTO1 || this.isTO2 || this.isAgency)
            )
        }
    }

    export default toNative(OrderDetailsSummary)
</script>
